import { useCallback, useState } from 'react';
import { useController } from 'react-hook-form';
import type { FieldValues, Path, UseControllerProps } from 'react-hook-form';

import { env } from '../../env.ts';
import { getAuthHeaders } from '../../services/directusClient/directusClient.ts';
import { parseError } from '../reactHookFormMantine/lib/parseError.ts';
import { ImageDropzone } from './image-upload/ImageDropzone.tsx';
import { ImageEditorModal } from './image-upload/ImageEditorModal.tsx';
import type { CropData } from './image-upload/ImageEditorModal.tsx';

type ImageUploadProps<T extends FieldValues> = UseControllerProps<T>;

export const ImageUpload = <T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
}: ImageUploadProps<T>) => {
  const {
    field: { value, onChange },
    fieldState,
  } = useController<T>({ name, control, defaultValue, rules, shouldUnregister });

  const {
    field: { value: originalValue, onChange: onChangeOriginal },
  } = useController<T>({ name: 'image_original' as Path<T>, control });

  const {
    field: { value: cropDataValue, onChange: onChangeCropData },
  } = useController<T>({ name: 'image_crop_data' as Path<T>, control });

  const [modalOpened, setModalOpened] = useState(false);
  const [modalMode, setModalMode] = useState<'edit-crop' | 'new'>('new');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoadingOriginal, setIsLoadingOriginal] = useState(false);
  const [localPreviewSrc, setLocalPreviewSrc] = useState<string | null>(null);
  const [justUploaded, setJustUploaded] = useState(false);

  // New file dropped — read it and open modal in 'new' mode
  const handleFileDrop = useCallback((file: File) => {
    setSelectedFile(file);
    setModalMode('new');
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageSrc(reader.result as string);
      setModalOpened(true);
    };
    reader.readAsDataURL(file);
  }, []);

  // Edit crop — load image from Directus and open modal in 'edit-crop' mode
  const handleEditCrop = useCallback(() => {
    const imageId = originalValue || value;
    if (!imageId) return;

    setIsLoadingOriginal(true);
    setModalMode('edit-crop');

    const imageUrl = `${env.VITE_DIRECTUS_API_URL}/assets/${imageId}`;
    setImageSrc(imageUrl);

    const loadOriginal = async () => {
      try {
        const res = await fetch(imageUrl, { headers: await getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load image');
        const blob = await res.blob();
        const file = new File([blob], 'original-image.png', { type: blob.type });
        setSelectedFile(file);
        setModalOpened(true);
      } catch {
        setImageSrc(null);
      } finally {
        setIsLoadingOriginal(false);
      }
    };

    void loadOriginal();
  }, [originalValue, value]);

  // Replace — reset form to empty dropzone
  const handleReplace = useCallback(() => {
    onChange('');
    onChangeOriginal('');
    onChangeCropData(null);
    setSelectedFile(null);
    setImageSrc(null);
    setLocalPreviewSrc(null);
    setJustUploaded(false);
  }, [onChange, onChangeOriginal, onChangeCropData]);

  // Upload complete — update form values, store preview and crop data
  const handleComplete = useCallback(
    (croppedFileId: string, originalFileId: string | null, previewSrc?: string, cropData?: CropData) => {
      onChange(croppedFileId);
      if (originalFileId) {
        onChangeOriginal(originalFileId);
      }
      if (previewSrc) setLocalPreviewSrc(previewSrc);
      if (cropData) {
        onChangeCropData(cropData);
      }
      setJustUploaded(true);
    },
    [onChange, onChangeOriginal, onChangeCropData],
  );

  const handleModalClose = useCallback(() => {
    setModalOpened(false);
    setSelectedFile(null);
    setImageSrc(null);
  }, []);

  const error = parseError('Image', fieldState.error);

  return (
    <>
      <ImageDropzone
        croppedImageId={value || null}
        error={error}
        isLoadingOriginal={isLoadingOriginal}
        justUploaded={justUploaded}
        localPreviewSrc={localPreviewSrc}
        onEditCrop={handleEditCrop}
        onFileDrop={handleFileDrop}
        onReplace={handleReplace}
      />

      <ImageEditorModal
        existingOriginalId={originalValue || null}
        imageSrc={imageSrc}
        initialCropData={cropDataValue ?? null}
        mode={modalMode}
        opened={modalOpened}
        originalFile={selectedFile}
        onClose={handleModalClose}
        onComplete={handleComplete}
      />
    </>
  );
};
